import React from 'react';
import { Loader2, MessageSquare, CheckCircle, XCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface MessageRequest {
  id: string;
  sender_id: string;
  message: string;
  created_at: string;
  sender?: {
    id: string;
    full_name: string;
    username?: string;
    avatar_url?: string;
  };
}

interface MessageRequestsPanelProps {
  requests: MessageRequest[];
  onAcceptRequest: (requestId: string) => void;
  onDeclineRequest: (requestId: string) => void;
  isLoading?: boolean;
}

const MessageRequestsPanel: React.FC<MessageRequestsPanelProps> = ({
  requests,
  onAcceptRequest,
  onDeclineRequest,
  isLoading = false
}) => {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary dark:text-golden" />
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="text-center py-8 px-4">
        <MessageSquare className="h-12 w-12 text-light-subtle dark:text-dark-subtle mx-auto mb-3 opacity-50" />
        <p className="text-light-text dark:text-dark-text font-medium">No message requests</p>
        <p className="text-sm text-light-subtle dark:text-dark-subtle mt-1">
          Message requests will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {requests.map((request) => (
        <div
          key={request.id}
          className="bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-xl p-4"
        >
          <div className="flex items-start gap-3 mb-3">
            {/* Avatar */}
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-golden/20 dark:from-primary/30 dark:to-golden/30 flex items-center justify-center overflow-hidden shrink-0">
              {request.sender?.avatar_url ? (
                <img
                  src={request.sender.avatar_url}
                  alt={request.sender.full_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-sm font-semibold text-primary dark:text-golden">
                  {request.sender?.full_name?.charAt(0).toUpperCase() || '?'}
                </span>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-light-text dark:text-dark-text truncate">
                {request.sender?.full_name || 'Unknown User'}
              </h4>
              <p className="text-xs text-light-subtle dark:text-dark-subtle mt-0.5">
                {formatDistanceToNow(new Date(request.created_at), {
                  addSuffix: true
                })}
              </p>
            </div>
          </div>

          {/* Message */}
          <div className="mb-3 p-2.5 bg-light-card dark:bg-dark-card rounded-lg border border-light-border dark:border-dark-border">
            <p className="text-sm text-light-text dark:text-dark-text break-words">
              {request.message}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={() => onAcceptRequest(request.id)}
              className="flex-1 px-3 py-2 bg-primary dark:bg-golden text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-1.5"
            >
              <CheckCircle className="h-4 w-4" />
              Accept
            </button>
            <button
              onClick={() => onDeclineRequest(request.id)}
              className="flex-1 px-3 py-2 bg-light-border dark:bg-dark-border text-light-text dark:text-dark-text rounded-lg text-sm font-medium hover:bg-light-bg dark:hover:bg-dark-bg transition-colors flex items-center justify-center gap-1.5"
            >
              <XCircle className="h-4 w-4" />
              Decline
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default MessageRequestsPanel;
